import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    WritableSignal,
    effect,
    inject,
    signal,
} from "@angular/core";
import { TargetGroupStore } from "../../../stores/target-groups/target-group.store";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import {
    TargetGroupForm,
    TargetGroupLimitedParameterForm,
    TargetGroupParameterForm,
} from "../../../models/target-groups/target-group-forms";
import { TargetGroupViewModel } from "@playcharge/common/models/backend/target-groups/target-group-view-model";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { Sex } from "@playcharge/common/models/backend/profile/sex";
import { MarketingSource } from "@playcharge/common/models/backend/target-groups/marketing-source";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatOption, MatSelect } from "@angular/material/select";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { GameCategoriesStore } from "../../../stores/offers/game-categories.store";
import { MatChipInputEvent, MatChipsModule } from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { TargetGroupEditModel } from "@playcharge/common/models/backend/target-groups/target-group-edit-model";
import { Guid } from "guid-typescript";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { ComponentRoutes } from "../../../models/constants/component-routes";

@Component({
    selector: "app-target-group",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatCheckbox,
        MatExpansionModule,
        MatSelect,
        MatOption,
        MatIcon,
        MatButton,
        MatChipsModule,
        EnumKeyValuePipe,
    ],
    templateUrl: "./target-group.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [TargetGroupStore],
})
export class TargetGroupComponent {
    private _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private _router: Router = inject(Router);
    private _toastrService: ToastrService = inject(ToastrService);
    public targetGroupStore: TargetGroupStore = inject(TargetGroupStore);
    public gameCategoriesStore = inject(GameCategoriesStore);

    public limitedForms: WritableSignal<
        { name: string; form: FormGroup<TargetGroupLimitedParameterForm<number>> }[]
    > = signal([]);

    public form: FormGroup<TargetGroupForm> | null = null;

    public Sex: typeof Sex = Sex;

    constructor() {
        effect(
            () => {
                const targetGroup: Nullable<TargetGroupViewModel> = this.targetGroupStore.group();

                this.initForm(targetGroup);

                this.limitedForms.set([
                    { name: "Age", form: this.form!.controls.age },
                    { name: "Earnings", form: this.form!.controls.earnings },
                    { name: "Payments", form: this.form!.controls.payments },
                ]);

                this._changeDetectorRef.markForCheck();
            },
            { allowSignalWrites: true },
        );
    }

    public addChip(event: MatChipInputEvent, controlPath: string): void {
        const value: string = event.value.trim();
        const tagsInput: AbstractControl<Nullable<string[]>> = this.form!.get(
            controlPath,
        ) as AbstractControl<Nullable<string[]>>;

        if (tagsInput.value) {
            tagsInput.patchValue([...tagsInput.value, value]);
        } else {
            tagsInput.patchValue([value]);
        }

        event.chipInput.clear();
    }

    public removeChip(chip: string, controlPath: string): void {
        const tagsInput: AbstractControl<Nullable<string[]>> = this.form!.get(
            controlPath,
        ) as AbstractControl<Nullable<string[]>>;

        if (tagsInput.value) {
            const index = tagsInput.value.indexOf(chip);

            if (index >= 0) {
                tagsInput.patchValue([
                    ...tagsInput.value.slice(0, index),
                    ...tagsInput.value.slice(index + 1),
                ]);
            }
        }
    }

    public resetForm(event: MatCheckboxChange, form: FormGroup): void {
        if (!event.checked) {
            form.reset({
                include: false,
                isMandatory: false,
            });
        }
    }

    public save(): void {
        const formData = this.form!.getRawValue();

        const editModel: TargetGroupEditModel = {
            id: formData.id,
            name: formData.name,
            parsingId: formData.parsingId,
            age: formData.age.include ? formData.age : null,
            sex: formData.sex.include ? formData.sex : null,
            gameCategoryIds: formData.gameCategoryIds.include ? formData.gameCategoryIds : null,
            deviceModels: formData.deviceModels.include ? formData.deviceModels : null,
            operationSystemVersions: formData.operationSystemVersions.include
                ? formData.operationSystemVersions
                : null,
            isMobile: formData.isMobile.include ? formData.isMobile : null,
            marketingSources: formData.marketingSources.include ? formData.marketingSources : null,
            isPayer: formData.isPayer.include ? formData.isPayer : null,
            payments: formData.payments.include ? formData.payments : null,
            earnings: formData.earnings.include ? formData.earnings : null,
        };

        this.targetGroupStore.updateGroup(editModel).subscribe({
            next: (group: TargetGroupViewModel) => {
                if (this.targetGroupStore.groupId()) {
                    this._toastrService.success("Successfully updated");
                } else {
                    this._router
                        .navigateByUrl(
                            ComponentRoutes.targetGroups.group.replace(
                                ":groupId",
                                group.id.toString(),
                            ),
                        )
                        .then(() => this._toastrService.success("Successfully created"));
                }
            },
            error: (error) => {
                this._toastrService.error(error.error.description ?? "Unknown Error");
            },
        });
    }

    private initForm(targetGroup: Nullable<TargetGroupViewModel>): void {
        this.form = new FormGroup<TargetGroupForm>({
            id: new FormControl<number>(targetGroup?.id ?? 0, { nonNullable: true }),
            name: new FormControl<Nullable<string>>(targetGroup?.name),
            parsingId: new FormControl<Nullable<string>>(
                targetGroup?.parsingId ?? Guid.create().toString(),
            ),
            age: new FormGroup<TargetGroupLimitedParameterForm<number>>({
                include: new FormControl<boolean>(!!targetGroup?.age, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.age?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormGroup({
                    min: new FormControl<Nullable<number>>(targetGroup?.age?.value?.min),
                    max: new FormControl<Nullable<number>>(targetGroup?.age?.value?.max),
                }),
            }),
            sex: new FormGroup<TargetGroupParameterForm<Sex[]>>({
                include: new FormControl<boolean>(!!targetGroup?.sex, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.sex?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormControl<Nullable<Sex[]>>(targetGroup?.sex?.value),
            }),
            gameCategoryIds: new FormGroup<TargetGroupParameterForm<number[]>>({
                include: new FormControl<boolean>(!!targetGroup?.gameCategoryIds, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(
                    targetGroup?.gameCategoryIds?.isMandatory ?? false,
                    { nonNullable: true },
                ),
                value: new FormControl<Nullable<number[]>>(targetGroup?.gameCategoryIds?.value),
            }),
            deviceModels: new FormGroup<TargetGroupParameterForm<string[]>>({
                include: new FormControl<boolean>(!!targetGroup?.deviceModels, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(
                    targetGroup?.deviceModels?.isMandatory ?? false,
                    { nonNullable: true },
                ),
                value: new FormControl<Nullable<string[]>>(targetGroup?.deviceModels?.value),
            }),
            operationSystemVersions: new FormGroup<TargetGroupParameterForm<string[]>>({
                include: new FormControl<boolean>(!!targetGroup?.operationSystemVersions, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(
                    targetGroup?.operationSystemVersions?.isMandatory ?? false,
                    { nonNullable: true },
                ),
                value: new FormControl<Nullable<string[]>>(
                    targetGroup?.operationSystemVersions?.value,
                ),
            }),
            isMobile: new FormGroup<TargetGroupParameterForm<boolean>>({
                include: new FormControl<boolean>(!!targetGroup?.isMobile, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.isMobile?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormControl<Nullable<boolean>>(targetGroup?.isMobile?.value),
            }),
            marketingSources: new FormGroup<TargetGroupParameterForm<MarketingSource[]>>({
                include: new FormControl<boolean>(!!targetGroup?.marketingSources, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(
                    targetGroup?.marketingSources?.isMandatory ?? false,
                    { nonNullable: true },
                ),
                value: new FormControl<Nullable<MarketingSource[]>>(
                    targetGroup?.marketingSources?.value,
                ),
            }),
            isPayer: new FormGroup<TargetGroupParameterForm<boolean>>({
                include: new FormControl<boolean>(!!targetGroup?.isPayer, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.isPayer?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormControl<Nullable<boolean>>(targetGroup?.isPayer?.value),
            }),
            payments: new FormGroup<TargetGroupLimitedParameterForm<number>>({
                include: new FormControl<boolean>(!!targetGroup?.payments, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.payments?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormGroup({
                    min: new FormControl<Nullable<number>>(targetGroup?.payments?.value?.min),
                    max: new FormControl<Nullable<number>>(targetGroup?.payments?.value?.min),
                }),
            }),
            earnings: new FormGroup<TargetGroupLimitedParameterForm<number>>({
                include: new FormControl<boolean>(!!targetGroup?.earnings, {
                    nonNullable: true,
                }),
                isMandatory: new FormControl<boolean>(targetGroup?.earnings?.isMandatory ?? false, {
                    nonNullable: true,
                }),
                value: new FormGroup({
                    min: new FormControl<Nullable<number>>(targetGroup?.earnings?.value?.min),
                    max: new FormControl<Nullable<number>>(targetGroup?.earnings?.value?.max),
                }),
            }),
        });
    }
}
